import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "@/api/client";

export type Me = {
  id: number;
  username: string;
  email: string | null;
  role: string;
  totp_enrolled: boolean;
  totp_ok: boolean;
};

type AuthState = {
  me: Me | null;
  loading: boolean;
  pendingTotp: boolean;
  // Captured from the login response. Used by the TOTP gate to choose
  // verify-code vs enroll when `me` hasn't loaded yet (e.g. transient cookie
  // delivery failure on the post-login refresh — audit finding #2).
  pendingTotpEnrolled: boolean;
  refresh: () => Promise<void>;
  login: (username: string, password: string) => Promise<{ totp_required: boolean }>;
  verifyTotp: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  setPendingTotp: (v: boolean) => void;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingTotp, setPendingTotp] = useState(false);
  const [pendingTotpEnrolled, setPendingTotpEnrolled] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api<Me>("/api/admin/auth/me");
      setMe(data);
    } catch {
      setMe(null);
      // Critical: do NOT window.location.reload() here — that's an infinite loop on 401
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(
    async (username: string, password: string) => {
      const r = await api<{ totp_required: boolean; totp_enrolled: boolean }>(
        "/api/admin/auth/login",
        { method: "POST", json: { username, password } }
      );
      setPendingTotp(r.totp_required);
      setPendingTotpEnrolled(r.totp_enrolled);  // belt for the next-gate UI; see comment above
      // Always refresh — the cookie is issued in both branches (totp_ok=false when
      // TOTP is still pending). /me does not require totp_ok, so this is safe and
      // populates `me` so the TOTP gate can read `me.totp_enrolled` to choose
      // verify-code vs enroll. Without this refresh, an already-enrolled user
      // sees the enroll form, which 409s on every submit. If the refresh itself
      // fails (transient cookie delivery / network), pendingTotpEnrolled above
      // is the fallback the TOTP gate uses to still pick the right form.
      await refresh();
      return r;
    },
    [refresh]
  );

  const verifyTotp = useCallback(
    async (code: string) => {
      await api("/api/admin/auth/totp/verify", { method: "POST", json: { code } });
      setPendingTotp(false);
      setPendingTotpEnrolled(false);
      await refresh();
    },
    [refresh]
  );

  const logout = useCallback(async () => {
    try {
      await api("/api/admin/auth/logout", { method: "POST" });
    } catch {
      /* best-effort */
    }
    setMe(null);
    setPendingTotp(false);
    setPendingTotpEnrolled(false);
  }, []);

  return (
    <Ctx.Provider
      value={{ me, loading, pendingTotp, pendingTotpEnrolled, refresh, login, verifyTotp, logout, setPendingTotp }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
